import { useState } from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner
} from '@shopify/polaris';

/**
 * ShareCard - Viral distribution mechanic
 * Allows merchants to share their profit wins
 * Creates organic word-of-mouth for ProfitPulse AI
 */
export default function ShareCard({ stats, shopData, showToast }) {
  const [shared, setShared] = useState(false);

  const avgMargin = parseFloat(stats?.avg_margin || 0);
  const totalProfit = parseFloat(stats?.total_profit || 0);

  // Only show when there's something positive to share
  if (avgMargin <= 0 || totalProfit <= 0) return null;

  const shareText = avgMargin >= 30
    ? `My Shopify store is protecting ${avgMargin.toFixed(0)}% profit margins with Profit Pulse AI.`
    : `I found ${parseInt(stats?.negative_margin_count || 0)} negative-margin orders with Profit Pulse AI and fixed profit leaks I would have missed.`;

  const handleShare = async (platform) => {
    const url = 'https://apps.shopify.com/profit-pulse-ai';
    let shareUrl = '';

    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(`${shareText}\n\n${url}`);
          showToast('Copied to clipboard!');
          setShared(true);
          return;
        } catch {
          showToast('Failed to copy', true);
          return;
        }
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShared(true);
  };

  return (
    <Card>
      <BlockStack gap="300">
        <Text variant="headingMd" as="h2">Share Your Win</Text>
        {shared ? (
          <Banner tone="success">
            <p>Thanks for sharing! You&apos;re helping other merchants discover margin protection.</p>
          </Banner>
        ) : (
          <BlockStack gap="200">
            <Text variant="bodySm" as="p" tone="subdued">
              {avgMargin >= 30
                ? 'Your margins are strong. Share the win with other merchants.'
                : 'You found hidden profit leaks. Help other merchants catch theirs sooner.'}
            </Text>
            <InlineStack gap="200">
              <Button size="slim" onClick={() => handleShare('twitter')}>Share on X</Button>
              <Button size="slim" onClick={() => handleShare('linkedin')}>Share on LinkedIn</Button>
              <Button size="slim" variant="plain" onClick={() => handleShare('copy')}>Copy Link</Button>
            </InlineStack>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}
